import React, { useState, useEffect } from "react";

const BenefitsFAQSection = () => {
  const faqs = [
    {
      question: "What services do we provide?",
      answer: "We provide comprehensive HR solutions including recruitment, compliance management, benefits administration, payroll support, and strategic HR consulting tailored to your business needs."
    },
    {
      question: "How can you help us with recruitment?",
      answer: "Our recruitment services include talent sourcing, candidate screening, interview coordination, and onboarding support to help you find the perfect fit for your organization."
    },
    {
      question: "What does your retainer program include?",
      answer: "Our retainer program offers ongoing HR support, unlimited consultations, policy development, compliance reviews, and priority access to our HR professionals."
    },
    {
      question: "What is the benefit of outsourcing my HR?",
      answer: "Outsourcing HR reduces costs, ensures compliance, provides expert guidance, and allows you to focus on core business operations while we handle complex HR functions."
    },
    {
      question: "How do we improve our turnover rate?",
      answer: "We analyze your current processes, implement retention strategies, improve employee engagement, enhance compensation structures, and create a positive workplace culture."
    }
  ];

  const [openIndex, setOpenIndex] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div style={styles.cofiContainer}>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Raleway:wght@400;600;700;800&display=swap');
          
          @keyframes cofiFadeInUp {
            from {
              opacity: 0;
              transform: translateY(40px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes cofiFadeInLeft {
            from {
              opacity: 0;
              transform: translateX(-40px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          
          @keyframes cofiFadeInRight {
            from {
              opacity: 0;
              transform: translateX(40px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          
          @keyframes cofiScaleIn {
            from {
              opacity: 0;
              transform: scale(0.95);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
          
          @keyframes cofiSlideDown {
            from {
              opacity: 0;
              max-height: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              max-height: 500px;
              transform: translateY(0);
            }
          }
          
          @keyframes cofiBlobMove {
            0%, 100% {
              transform: translate(0, 0) scale(1);
            }
            33% {
              transform: translate(40px, -40px) scale(1.15);
            }
            66% {
              transform: translate(-30px, 30px) scale(0.95);
            }
          }
          
          @keyframes cofiShine {
            0% {
              left: -150%;
            }
            50%, 100% {
              left: 150%;
            }
          }
          
          .cofi-faq-item:hover {
            transform: translateX(5px);
          }
          
          .cofi-stat-badge:hover {
            transform: scale(1.08);
          }
          
          .cofi-feature-icon:hover {
            animation: cofiPulse 0.6s ease;
          }
          
          @keyframes cofiPulse {
            0%, 100% {
              transform: scale(1);
            }
            50% {
              transform: scale(1.15);
            }
          }
        `}
      </style>
      
      <div style={{
        ...styles.cofiWrapper,
        opacity: isVisible ? 1 : 0,
        animation: isVisible ? 'cofiFadeInUp 0.8s ease forwards' : 'none'
      }}>
        {/* Left Content - FAQ Section */}
        <div style={{
          ...styles.cofiLeftSection,
          animation: isVisible ? 'cofiFadeInLeft 1s ease forwards 0.2s' : 'none',
          opacity: isVisible ? 1 : 0
        }}>
          <div style={styles.cofiFaqContainer}>
            <div style={styles.cofiFaqHeader}>
              <div style={{
                ...styles.cofiBadge,
                animation: isVisible ? 'cofiScaleIn 0.6s ease forwards 0.4s' : 'none',
                opacity: isVisible ? 1 : 0
              }}>
                Common Questions
              </div>
              <h2 style={styles.cofiFaqTitle}>Frequently Asked Questions</h2>
              <p style={styles.cofiFaqSubtitle}>Find answers to the most common inquiries about our HR services</p>
            </div>

            <div style={styles.cofiFaqList}>
              {faqs.map((faq, index) => (
                <div 
                  key={index}
                  className="cofi-faq-item"
                  style={{
                    ...styles.cofiFaqItem,
                    ...(openIndex === index ? styles.cofiFaqItemActive : {}),
                    animation: isVisible ? `cofiScaleIn 0.6s ease forwards ${0.6 + index * 0.1}s` : 'none',
                    opacity: isVisible ? 1 : 0
                  }}
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    style={styles.cofiFaqButton}
                  >
                    <span style={styles.cofiFaqQuestion}>{faq.question}</span>
                    <span style={{
                      ...styles.cofiFaqIcon,
                      transform: openIndex === index ? 'rotate(180deg)' : 'rotate(0deg)'
                    }}>
                      ▼
                    </span>
                  </button>
                  
                  {openIndex === index && (
                    <div style={{
                      ...styles.cofiFaqAnswer,
                      animation: 'cofiSlideDown 0.3s ease forwards'
                    }}>
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Content Section */}
        <div style={{
          ...styles.cofiRightSection,
          animation: isVisible ? 'cofiFadeInRight 1s ease forwards 0.3s' : 'none',
          opacity: isVisible ? 1 : 0
        }}>
          <h1 style={styles.cofiMainTitle}>
            <span style={styles.cofiHighlight}>The Benefits</span> of Humanex
          </h1>

          <p style={styles.cofiDescription}>
            Transform your HR operations with our comprehensive solutions. We deliver expert guidance, 
            streamlined processes, and strategic support.
          </p>

          <div style={styles.cofiStatsGrid}>
            {[
              { value: '500+', label: 'Clients Served', icon: '' },
              { value: '98%', label: 'Success Rate', icon: '' },
              { value: '24/7', label: 'Support Available', icon: '' }
            ].map((stat, idx) => (
              <div 
                key={idx}
                className="cofi-stat-badge"
                style={{
                  ...styles.cofiStatBadge,
                  animation: isVisible ? `cofiScaleIn 0.6s ease forwards ${1.0 + idx * 0.1}s` : 'none',
                  opacity: isVisible ? 1 : 0
                }}
              >
                <div style={styles.cofiStatIcon}>{stat.icon}</div>
                <div style={styles.cofiStatValue}>{stat.value}</div>
                <div style={styles.cofiStatLabel}>{stat.label}</div>
              </div>
            ))}
          </div>

          <div style={styles.cofiFeaturesList}>
            <h3 style={styles.cofiFeaturesTitle}>Why Choose Us?</h3>
            {[
              { icon: '✓', text: 'Expert HR Consultation & Strategy' },
              { icon: '✓', text: 'Comprehensive Compliance Management' },
              { icon: '✓', text: 'Advanced Recruitment Solutions' },
              { icon: '✓', text: 'Employee Relations Support' },
            ].map((feature, idx) => (
              <div 
                key={idx}
                style={{
                  ...styles.cofiFeatureItem,
                  animation: isVisible ? `cofiFadeInRight 0.6s ease forwards ${1.3 + idx * 0.1}s` : 'none',
                  opacity: isVisible ? 1 : 0
                }}
              >
                <span className="cofi-feature-icon" style={styles.cofiFeatureIcon}>{feature.icon}</span>
                <span style={styles.cofiFeatureText}>{feature.text}</span>
              </div>
            ))}
          </div>

          {/* <button style={styles.cofiCtaButton}>
            Get Started Today →
          </button> */}
        </div>
      </div>

      {/* Background decorative elements */}
      <div style={{
        ...styles.cofiBgBlob1,
        animation: 'cofiBlobMove 20s ease-in-out infinite'
      }}></div>
      <div style={{
        ...styles.cofiBgBlob2,
        animation: 'cofiBlobMove 25s ease-in-out infinite reverse'
      }}></div>
    </div>
  );
};

const styles = {
  cofiContainer: {
    minHeight: '100vh',
    background: '#ffffff',
    padding: '80px 20px',
    fontFamily: '"Raleway", sans-serif',
    position: 'relative',
    overflow: 'hidden',
    marginTop: '-180px',
  },
  cofiWrapper: {
    maxWidth: '1300px',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '80px',
    alignItems: 'start',
    position: 'relative',
    zIndex: 2,
  },
  cofiLeftSection: {
    position: 'sticky',
    top: '50px',
    textAlign: 'justify',
  },
  cofiFaqContainer: {
    background: '#ffffff',
    borderRadius: '28px',
    padding: '45px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.12)',
    border: '2px solid #f5f5f5',
  },
  cofiFaqHeader: {
    marginBottom: '35px',
  },
  cofiBadge: {
    display: 'inline-block',
    padding: '8px 20px',
    background: 'linear-gradient(135deg, #c62828)',
    color: 'white',
    borderRadius: '50px',
    fontSize: '12px',
    fontWeight: '700',
    marginBottom: '20px',
    letterSpacing: '1px',
    // boxShadow: '0 4px 15px rgba(198, 40, 40, 0.3)',
    textTransform: 'uppercase',
  },
  cofiFaqTitle: {
    fontSize: '34px',
    fontWeight: '800',
    color: '#1e2330',
    marginBottom: '12px',
    letterSpacing: '-0.5px',
    fontFamily: '"Raleway", sans-serif',
  },
  cofiFaqSubtitle: {
    fontSize: '15px',
    color: '#777',
    fontWeight: '500',
    lineHeight: '1.6',
  },
  cofiFaqList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  cofiFaqItem: {
    background: '#fafafa',
    borderRadius: '14px',
    overflow: 'hidden',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    border: '2px solid transparent',
  },
  cofiFaqItemActive: {
    background: '#fff5f5',
    borderColor: '#c62828',
    boxShadow: '0 6px 20px rgba(198, 40, 40, 0.18)',
  },
  cofiFaqButton: {
    width: '100%',
    padding: '22px 24px',
    background: 'none',
    border: 'none',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    textAlign: 'left',
  },
  cofiFaqQuestion: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#1e2330',
    flex: 1,
    fontFamily: '"Raleway", sans-serif',
    letterSpacing: '0.2px',
  },
  cofiFaqIcon: {
    fontSize: '13px',
    color: '#c62828',
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    fontWeight: 'bold',
  },
  cofiFaqAnswer: {
    padding: '0 24px 22px 24px',
    fontSize: '14px',
    color: '#666',
    lineHeight: '1.8',
    fontFamily: '"Raleway", sans-serif',
    fontWeight: '400',
  },
  cofiRightSection: {
    paddingTop: '40px',
  },
  cofiMainTitle: {
    fontSize: '50px',
    fontWeight: '800',
    color: '#1e2330',
    lineHeight: '1.2',
    marginBottom: '28px',
    letterSpacing: '-1.5px',
    fontFamily: '"Raleway", sans-serif',
        textAlign: 'justify',

  },
  cofiHighlight: {
       color: '#c62828',

  },
  cofiDescription: {
    fontSize: '17px',
    color: '#1e2330',
    lineHeight: '1.8',
    marginBottom: '45px',
    fontFamily: '"Raleway", sans-serif',
    fontWeight: '500',
        textAlign: 'justify',

  },
  cofiStatsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '20px',
    marginBottom: '45px',
  },
  cofiStatBadge: {
    background: '#ffffff',
    padding: '24px 16px',
    borderRadius: '16px',
    textAlign: 'center',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: '2px solid #c62828',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
  },
  cofiStatIcon: {
    fontSize: '32px',
    marginBottom: '8px',
  },
  cofiStatValue: {
    fontSize: '28px',
    fontWeight: '800',
          color: '#c62828',

    marginBottom: '6px',
    fontFamily: '"Raleway", sans-serif',
  },
  cofiStatLabel: {
    fontSize: '12px',
    color: '#666',
    fontWeight: '600',
    letterSpacing: '0.5px',
  },
  cofiFeaturesList: {
    marginBottom: '40px',
  },
  cofiFeaturesTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e2330',
    marginBottom: '24px',
    fontFamily: '"Raleway", sans-serif',
        textAlign: 'justify',
  },
  cofiFeatureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    fontSize: '15px',
    color: '#1e2330',
    fontFamily: '"Raleway", sans-serif',
    fontWeight: '600',
    marginBottom: '16px',
    transition: 'transform 0.3s ease',
        textAlign: 'justify',
  },
  cofiFeatureIcon: {
    width: '26px',
    height: '26px',
    background: 'linear-gradient(135deg, #c62828 0%, #8b0000 100%)',
    color: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 'bold',
    flexShrink: 0,
    boxShadow: '0 2px 8px rgba(198, 40, 40, 0.3)',
    transition: 'all 0.3s ease',
  },
  cofiFeatureText: {
    flex: 1,
  },
  cofiCtaButton: {
    padding: '16px 38px',
    background: 'linear-gradient(135deg, #c62828 0%, #8b0000 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '14px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 4px 15px rgba(198, 40, 40, 0.35)',
    fontFamily: '"Raleway", sans-serif',
    letterSpacing: '0.5px',
    marginLeft: '-370px',
  },
  cofiBgBlob1: {
    position: 'absolute',
    width: '600px',
    height: '600px',
    background: 'radial-gradient(circle, rgba(198, 40, 40, 0.1) 0%, transparent 70%)',
    borderRadius: '50%',
    top: '-150px',
    right: '-150px',
    filter: 'blur(100px)',
    zIndex: 1,
  },
  cofiBgBlob2: {
    position: 'absolute',
    width: '700px',
    height: '700px',
    background: 'radial-gradient(circle, rgba(198, 40, 40, 0.08) 0%, transparent 70%)',
    borderRadius: '50%',
    bottom: '-200px',
    left: '-200px',
    filter: 'blur(120px)',
    zIndex: 1,
  },
};

// Add media query handling
const mediaQuery = window.matchMedia('(max-width: 992px)');
if (mediaQuery.matches) {
  styles.cofiWrapper.gridTemplateColumns = '1fr';
  styles.cofiWrapper.gap = '40px';
  styles.cofiStatsGrid.gridTemplateColumns = 'repeat(3, 1fr)';
  styles.cofiMainTitle.fontSize = '38px';
  styles.cofiLeftSection.position = 'relative';
  styles.cofiLeftSection.top = '0';
}

export default BenefitsFAQSection;